import type { DefaultBodyType } from 'msw'
import { http, HttpResponse } from 'msw/http'
import { createMockId, mockData, type MockCompactPlan } from './mockData'
import { toCompactPlan } from './compactPlan'
import type {
  AddNewsArticle,
  AnalysisType,
  AnalysisTypeSubstance,
  Ansatt,
  AvdelingResponse,
  CompactPlan,
  CreateAnalysisType,
  CreateAnalysisTypeSubstance,
  CreateContract,
  CreateLab,
  CreateMatrix,
  CreateOkProgram,
  CreatePayloadSchema,
  CreatePlan,
  CreateProduct,
  CreateRequisition,
  CreateSample,
  CreateSampleType,
  CreateSubstance,
  CreateTask,
  ReplaceTaskDelegation,
  CreatePurpose,
  CreateSubPurpose,
  Contract,
  ContractAnalysisType,
  GroupedTaskDelegation,
  Lab,
  ImportantMessage,
  ImportantMessageInput,
  Matrix,
  NewsArticle,
  NewsArticles,
  OkProgram,
  PayloadSchema,
  PlanSummary,
  Product,
  ProdusentResponse,
  Purpose,
  RegionResponse,
  Requisition,
  RequisitionResult,
  Sample,
  SampleType,
  SeksjonResponse,
  SingleGroupedTask,
  SinglePlan,
  SubPurpose,
  Substance,
  Task,
  TaskDelegation,
  TilsynsobjektResponseV2,
  UpdateNewsArticle,
  UpdateContract,
  UpdateContractAnalysisType,
  UpdatePayloadSchema,
  UpdateOkProgram,
  UpdatePlan,
  UpdatePurpose,
  UpdateSample,
  UpdateSubPurpose,
  UpdateTask,
  UpdateUserSettings
} from '../types/types'
import type { components } from '../types/prove-v1'
import { legislationHandlers } from './legislationHandlers'

type OwnerHierarchyIds = {
  regionIds: Set<string>
  avdelingIds: Set<string>
  seksjonIds: Set<string>
  teamIds: Set<string>
}

type SampleSortDirection = 'asc' | 'desc'

const createOwnerHierarchyIds = (): OwnerHierarchyIds => ({
  regionIds: new Set<string>(),
  avdelingIds: new Set<string>(),
  seksjonIds: new Set<string>(),
  teamIds: new Set<string>()
})

const collectRequestedOwnerIds = (ownerIds: string[]): OwnerHierarchyIds => {
  const hierarchyIds = createOwnerHierarchyIds()

  for (const id of ownerIds) {
    const [type, rawId] = id.split(':')
    if (!rawId) continue
    if (type === 'region') hierarchyIds.regionIds.add(rawId)
    else if (type === 'avdeling') hierarchyIds.avdelingIds.add(rawId)
    else if (type === 'seksjon') hierarchyIds.seksjonIds.add(rawId)
    else if (type === 'team') hierarchyIds.teamIds.add(rawId)
  }

  return hierarchyIds
}

const expandRegionOwnerIds = (hierarchyIds: OwnerHierarchyIds): void => {
  if (hierarchyIds.regionIds.size === 0) return

  for (const avdeling of mockData.avdelinger) {
    if (avdeling.regionId && hierarchyIds.regionIds.has(avdeling.regionId)) {
      hierarchyIds.avdelingIds.add(avdeling.id)
    }
  }
}

const expandAvdelingOwnerIds = (hierarchyIds: OwnerHierarchyIds): void => {
  if (hierarchyIds.avdelingIds.size === 0) return

  for (const seksjon of mockData.seksjoner) {
    if (
      seksjon.avdelingId &&
      hierarchyIds.avdelingIds.has(seksjon.avdelingId)
    ) {
      hierarchyIds.seksjonIds.add(seksjon.id)
    }
  }
}

const expandSeksjonOwnerIds = (hierarchyIds: OwnerHierarchyIds): void => {
  if (hierarchyIds.seksjonIds.size === 0) return

  for (const team of mockData.teams) {
    if (team.seksjonId && hierarchyIds.seksjonIds.has(team.seksjonId)) {
      hierarchyIds.teamIds.add(team.id.toString())
    }
  }
}

const toExpandedOwnerIds = (
  hierarchyIds: OwnerHierarchyIds,
  ownerIds: string[]
): string[] => {
  const expanded = new Set<string>()
  for (const avdelingId of hierarchyIds.avdelingIds) {
    expanded.add(`avdeling:${avdelingId}`)
  }
  for (const seksjonId of hierarchyIds.seksjonIds) {
    expanded.add(`seksjon:${seksjonId}`)
  }
  for (const teamId of hierarchyIds.teamIds) {
    expanded.add(`team:${teamId}`)
  }
  for (const id of ownerIds) {
    const [type] = id.split(':')
    if (type === 'avdeling' || type === 'seksjon' || type === 'team') {
      expanded.add(id)
    }
  }

  return Array.from(expanded)
}

const notFound = <
  T extends DefaultBodyType = DefaultBodyType
>(): HttpResponse<T> =>
  new HttpResponse<T>(null, {
    status: 404,
    headers: { 'Content-Length': '0' }
  })

const noContent = <
  T extends DefaultBodyType = DefaultBodyType
>(): HttpResponse<T> => new HttpResponse<T>(null, { status: 204 })

const newEtag = (): string => `etag-${Date.now()}`

const isActiveNow = (
  item: { startDate: string; endDate: string },
  now = new Date()
): boolean => new Date(item.startDate) <= now && new Date(item.endDate) >= now

const toGroupedTaskDelegation = (task: Task): GroupedTaskDelegation => ({
  id: task.id,
  parentDelegationId: task.id,
  ownerId: task.ownerId,
  quantity: task.quantity ?? 0,
  directCount: task.directCount ?? 0,
  propagatedCount: task.propagatedCount ?? 0,
  totalCount: task.totalCount ?? 0,
  etag: task.etag
})

const toSingleGroupedTask = (task: Task): SingleGroupedTask => ({
  id: task.id,
  planIds: task.planIds ?? [],
  title: task.title,
  analysisType: task.analysisType,
  lab: task.lab ?? mockData.labs[0],
  sampntType: task.sampntType,
  description: task.description,
  planStatus: task.planStatus ?? 'draft',
  countSubSamples: task.countSubSamples ?? false,
  delegations: [toGroupedTaskDelegation(task)],
  ordningsVerdi: task.ordningsVerdi,
  locationRequired: task.locationRequired ?? false,
  analysisTypeApprovalStatus: task.analysisTypeApprovalStatus,
  approvalComment: task.approvalComment
})

const toSinglePlan = (plan: MockCompactPlan): SinglePlan => ({
  ...plan,
  contractRef: plan.contractRef ?? undefined,
  directDue:
    'directDue' in plan ? (plan.directDue ?? null) : (plan.due ?? null),
  directContractRef:
    'directContractRef' in plan
      ? (plan.directContractRef ?? null)
      : (plan.contractRef ?? null),
  directWorkspaceId:
    'directWorkspaceId' in plan
      ? (plan.directWorkspaceId ?? null)
      : (plan.workspaceId ?? null),
  directPurposeId:
    'directPurposeId' in plan
      ? (plan.directPurposeId ?? null)
      : (plan.purposeId ?? null),
  parents: [],
  children: [],
  tasks: mockData.tasks
    .filter((task) => task.planIds?.includes(plan.id))
    .map(toSingleGroupedTask),
  etag: plan.etag ?? 'etag-plan',
  samplingInstructions: plan.samplingInstructions ?? null,
  storageInstructions: plan.storageInstructions ?? null,
  labelingInstructions: plan.labelingInstructions ?? null,
  shippingInstructions: plan.shippingInstructions ?? null,
  followUpInstructions: plan.followUpInstructions ?? null
})

const toGroupedTask = (task: Task) => ({
  id: task.id,
  planIds: task.planIds ?? [],
  title: task.title,
  analysisTypeId: task.analysisType.id,
  labId: task.lab?.id ?? 0,
  sampntTypeId: task.sampntType.id,
  delegations: [toGroupedTaskDelegation(task)],
  planStatus: task.planStatus ?? 'draft',
  countSubSamples: task.countSubSamples ?? false,
  ordningsVerdi: task.ordningsVerdi,
  locationRequired: task.locationRequired ?? false,
  analysisTypeApprovalStatus: task.analysisTypeApprovalStatus,
  quantity: task.quantity,
  totalCount: task.totalCount,
  directCount: task.directCount,
  propagatedCount: task.propagatedCount
})

const replacePlanTask = (task: Task): void => {
  for (const plan of mockData.plans.plans) {
    if (!task.planIds?.includes(plan.id)) continue
    const groupedTask = toGroupedTask(task)
    const tasks = plan.tasks ?? []
    const index = tasks.findIndex((t) => t.id === task.id)
    if (index === -1) tasks.push(groupedTask)
    else tasks[index] = groupedTask
    plan.tasks = tasks
  }
}

const buildTaskFromPayload = (
  body: CreateTask | UpdateTask,
  taskId = createMockId(),
  planIds: number[] = []
): Task | null => {
  const sampntType = mockData.sampntTypes.find(
    (sampntType) => sampntType.id === body.sampntTypeId
  )
  const analysisType = mockData.analysisTypes.find(
    (analysisType) => analysisType.id === body.analysisTypeId
  )
  const lab = mockData.labs.find((lab) => lab.id === body.labId)
  const ordningsVerdi = mockData.ordningsverdier.find(
    (ordningsVerdi) => ordningsVerdi.id === body.ordningsverdiId
  )
  if (ordningsVerdi == null || analysisType == null || sampntType == null) {
    return null
  }
  return {
    id: taskId,
    planIds,
    ownerId: body.ownerId,
    quantity: body.quantity,
    matrixAmount: body.matrixAmount,
    title: body.title,
    description: body.description,
    ordningsVerdi,
    analysisType,
    sampntType,
    lab,
    labReceptionAddressId: body.labReceptionAddressId,
    planStatus: body.planStatus,
    countSubSamples: body.countSubSamples,
    locationRequired: body.locationRequired,
    contractId: body.contractId,
    duedates: 'duedates' in body ? body.duedates : undefined,
    etag: newEtag()
  }
}

const getEffectiveOwnerIds = (url: URL): string[] => {
  const ownerIds = url.searchParams.getAll('ownerIds')
  if (ownerIds.length === 0) return []
  if (url.searchParams.get('ownerScope') !== 'subtree') return ownerIds

  const hierarchyIds = collectRequestedOwnerIds(ownerIds)
  expandRegionOwnerIds(hierarchyIds)
  expandAvdelingOwnerIds(hierarchyIds)
  expandSeksjonOwnerIds(hierarchyIds)
  return toExpandedOwnerIds(hierarchyIds, ownerIds)
}

const applyFilter = (
  samples: Sample[],
  values: string[],
  predicate: (sample: Sample) => boolean
): Sample[] => {
  return values.length === 0 ? samples : samples.filter(predicate)
}

const applyNullableFilter = (
  samples: Sample[],
  value: string | null,
  predicate: (sample: Sample) => boolean
): Sample[] => {
  return value == null ? samples : samples.filter(predicate)
}

const compareNullableValues = (
  left: string | number | null,
  right: string | number | null,
  direction: SampleSortDirection
): number => {
  if (left == null && right == null) return 0
  if (left == null) return direction === 'asc' ? -1 : 1
  if (right == null) return direction === 'asc' ? 1 : -1
  if (left < right) return direction === 'asc' ? -1 : 1
  if (left > right) return direction === 'asc' ? 1 : -1
  return 0
}

const sortSamples = (samples: Sample[], sortParam: string | null): Sample[] => {
  if (!sortParam) return samples

  const match = /^(sampleId|takenDate),(asc|desc)$/i.exec(sortParam)
  if (!match) return samples

  const field = match[1]
  const direction = match[2].toLowerCase() as SampleSortDirection

  return samples.slice().sort((a, b) => {
    if (field === 'sampleId') {
      return compareNullableValues(a.id ?? null, b.id ?? null, direction)
    }
    const aDate = a.takenDate ? new Date(a.takenDate).getTime() : null
    const bDate = b.takenDate ? new Date(b.takenDate).getTime() : null
    return compareNullableValues(aDate, bDate, direction)
  })
}

const filterSamples = (samples: Sample[], url: URL): Sample[] => {
  const effectiveOwnerIds = getEffectiveOwnerIds(url)
  const taskIds = url.searchParams.getAll('taskIds')
  const productIds = url.searchParams.getAll('productIds')
  const matrixIds = url.searchParams.getAll('matrixIds')
  const substanceIds = url.searchParams.getAll('substanceIds')
  const status = url.searchParams.getAll('status')
  const conclusion = url.searchParams.get('conclusion')
  const compromised = url.searchParams.get('compromised')
  const sampleIds = url.searchParams.getAll('sampleIds')
  const producerSearch = url.searchParams.get('producerSearch')?.trim()

  let result = applyFilter(samples, effectiveOwnerIds, (sample) => {
    const ownerId = mockData.tasks.find(
      (task) => task.id === sample.taskId
    )?.ownerId
    return ownerId != null && effectiveOwnerIds.includes(ownerId)
  })

  result = applyFilter(result, taskIds, (sample) =>
    taskIds.includes(sample.taskId?.toString() ?? '')
  )

  result = applyFilter(result, productIds, (sample) => {
    const productId = sample.sampleType?.productId?.toString()
    return productId != null && productIds.includes(productId)
  })

  result = applyFilter(result, matrixIds, (sample) => {
    const matrixId = sample.sampleType?.matrixId?.toString()
    return matrixId != null && matrixIds.includes(matrixId)
  })

  result = applyFilter(result, substanceIds, (sample) => {
    const analysisType = mockData.analysisTypes.find(
      (at) => at.id === sample.sampleType?.id
    )
    return (
      analysisType?.substances?.some((s) =>
        substanceIds.includes(s.substance.id.toString())
      ) ?? false
    )
  })

  result = applyFilter(result, status, (sample) => {
    const stateString = sample.state == null ? '' : sample.state.toString()
    return status.includes(stateString)
  })

  result = applyNullableFilter(
    result,
    conclusion,
    (sample) => sample.requisitions?.[0]?.results?.status === conclusion
  )

  result = applyNullableFilter(result, compromised, (sample) =>
    sample.subSamples && sample.subSamples.length > 0
      ? sample.subSamples.every((subSample) => subSample.compromised)
      : false
  )

  result = applyFilter(result, sampleIds, (sample) => {
    const trimmed = sampleIds.map((id) => id.trim())
    return sample.id != null && trimmed.some((id) => sample.id.startsWith(id))
  })

  result = applyNullableFilter(result, producerSearch ?? null, (sample) => {
    const matchedProducer = mockData.produsenter.find(
      (produsent) =>
        produsent.tilsynsobjektId === sample.oversightObject ||
        produsent.produsentnummer === sample.externalReferenceId
    )
    const tilsynsobjekt = mockData.tilsynsobjekter.find(
      (item) => item.idstring === sample.oversightObject
    )
    const search = (producerSearch ?? '').toLowerCase()

    return [
      matchedProducer?.virksomhetsNavn,
      matchedProducer?.produsentnummer,
      tilsynsobjekt?.virksomhetsnavn,
      sample.externalReferenceId,
      sample.oversightObject
    ].some((value) => value?.toLowerCase().includes(search) ?? false)
  })

  return sortSamples(result, url.searchParams.get('sort'))
}

export const handlers = [
  ...legislationHandlers,
  // plans
  http.post<never, CreatePlan, SinglePlan>(
    '/api/plans',
    async ({ request }) => {
      const createPlan = await request.json()
      const parent = mockData.plans.plans.find(
        (plan) => plan.id === createPlan.parentPlans?.[0]
      )
      const workspaceId = createPlan.workspaceId ?? parent?.workspaceId
      const plan: MockCompactPlan = {
        id: createMockId(),
        tasks: [],
        due: createPlan.due ?? parent?.due,
        directDue: createPlan.due ?? null,
        ownerId: createPlan.ownerId,
        description: createPlan.description,
        contractRef: createPlan.contractRef ?? parent?.contractRef,
        directContractRef: createPlan.contractRef ?? null,
        parents: createPlan.parentPlans ?? [],
        children: createPlan.subPlans ?? [],
        status: 'active',
        directCount: 0,
        propagatedCount: 0,
        totalCount: 0,
        directQuantity: 0,
        propagatedQuantity: 0,
        totalQuantity: 0,
        etag: newEtag(),
        samplingInstructions: null,
        storageInstructions: null,
        labelingInstructions: null,
        shippingInstructions: null,
        followUpInstructions: null,
        ...(createPlan.contractId != null && {
          contractId: createPlan.contractId
        }),
        ...(createPlan.okProgramId != null && {
          okProgramId: createPlan.okProgramId
        }),
        ...(workspaceId != null && {
          workspaceId
        }),
        directWorkspaceId: createPlan.workspaceId ?? null,
        fagseksjonWorkspaceId: createPlan.fagseksjonWorkspaceId,
        effectiveFagseksjonWorkspaceId:
          createPlan.fagseksjonWorkspaceId ??
          parent?.effectiveFagseksjonWorkspaceId ??
          parent?.fagseksjonWorkspaceId,
        directPurposeId: createPlan.purposeId ?? null,
        purposeId: createPlan.purposeId ?? parent?.purposeId
      }
      mockData.plans.plans.push(plan)
      return HttpResponse.json(toSinglePlan(plan), { status: 201 })
    }
  ),
  http.get('/api/plans', async ({ request }) => {
    const accept = request.headers.get('accept') ?? ''
    if (
      accept.includes('application/vnd.mattilsynet.proveta.v4.summary+json')
    ) {
      const planSummaries: PlanSummary[] = mockData.plans.plans.map((plan) => ({
        id: plan.id,
        name: plan.description ?? String(plan.id),
        status: plan.status,
        parents: plan.parents
      }))
      return HttpResponse.json(planSummaries)
    }

    return HttpResponse.json({
      ...mockData.plans,
      plans: mockData.plans.plans.map(toCompactPlan)
    })
  }),
  http.get('/api/workspaces', async () =>
    HttpResponse.json(mockData.workspaces)
  ),
  // The default mock identity is admin and sees both workspace kinds.
  http.get('/api/workspace-groups', () =>
    HttpResponse.json(
      ['FAGSEKSJON', 'TILSYNSDIVISJON'].map((code) => {
        const workspaces = mockData.workspaces.filter(
          (workspace) => workspace.active && workspace.kind?.code === code
        )
        return { kind: workspaces[0]?.kind, workspaces }
      })
    )
  ),
  http.post<never, CreateContract, Contract>(
    '/api/contracts',
    async ({ request }) => {
      const body = await request.json()
      const analysisTypes: ContractAnalysisType[] = (
        body.analysisTypeIds ?? []
      ).flatMap((analysisTypeInput) => {
        const analysisType = mockData.analysisTypes.find(
          (a) => a.id === analysisTypeInput.analysisTypeId
        )
        return analysisType
          ? [
              {
                id: createMockId(),
                analysisType,
                price: analysisTypeInput.price,
                mtApproval: false,
                labApproval: false,
                updatedTime: new Date().toISOString(),
                updatedBy: 'Testbruker 1'
              }
            ]
          : []
      })
      const contract: Contract = {
        id: createMockId(),
        title: body.title,
        labId: body.labId,
        analysisTypes,
        ownerId: body.ownerId,
        startDate: body.startDate,
        endDate: body.endDate,
        etag: newEtag()
      }
      mockData.contracts.push(contract)
      return HttpResponse.json(contract, { status: 201 })
    }
  ),
  http.get('/api/contracts', async () => HttpResponse.json(mockData.contracts)),
  http.get<{ contractId: string }>(
    '/api/contracts/:contractId',
    async ({ params }) => {
      const contract = mockData.contracts.find(
        (contract) => contract.id.toString() === params.contractId
      )
      return contract ? HttpResponse.json(contract) : notFound()
    }
  ),
  http.put<{ contractId: string }, UpdateContract, Contract>(
    '/api/contracts/:contractId',
    async ({ params, request }) => {
      const index = mockData.contracts.findIndex(
        (contract) => contract.id.toString() === params.contractId
      )
      if (index === -1) return notFound()
      const ifMatch = request.headers.get('If-Match')
      if (ifMatch !== mockData.contracts[index].etag) {
        return new HttpResponse(null, { status: 412 })
      }
      const body = await request.json()
      const updated = {
        ...mockData.contracts[index],
        ...body,
        etag: newEtag()
      }
      mockData.contracts[index] = updated
      return HttpResponse.json(updated)
    }
  ),
  http.delete<{ contractId: string }>(
    '/api/contracts/:contractId',
    async ({ params, request }) => {
      const contract = mockData.contracts.find(
        (contract) => contract.id.toString() === params.contractId
      )
      if (!contract) return notFound()
      const ifMatch = request.headers.get('If-Match')
      if (ifMatch !== contract.etag) {
        return new HttpResponse(null, { status: 412 })
      }
      mockData.contracts = mockData.contracts.filter(
        (contract) => contract.id.toString() !== params.contractId
      )
      return noContent()
    }
  ),
  http.get<{ contractId: string }>(
    '/api/contracts/:contractId/analysis-types',
    async ({ params }) => {
      const contract = mockData.contracts.find(
        (contract) => contract.id.toString() === params.contractId
      )
      return contract
        ? HttpResponse.json(contract.analysisTypes ?? [])
        : notFound()
    }
  ),
  http.post<
    { contractId: string },
    { analysisTypeId: number; price?: number },
    ContractAnalysisType
  >(
    '/api/contracts/:contractId/analysis-types',
    async ({ params, request }) => {
      const contract = mockData.contracts.find(
        (contract) => contract.id.toString() === params.contractId
      )
      if (!contract) return notFound()
      const body = await request.json()
      const analysisType = mockData.analysisTypes.find(
        (analysisType) => analysisType.id === body.analysisTypeId
      )
      if (!analysisType) return notFound()
      const contractAnalysisType: ContractAnalysisType = {
        id: createMockId(),
        analysisType,
        price: body.price,
        mtApproval: false,
        labApproval: false,
        updatedTime: new Date().toISOString(),
        updatedBy: 'Testbruker 1'
      }
      contract.analysisTypes = [
        ...(contract.analysisTypes ?? []),
        contractAnalysisType
      ]
      return HttpResponse.json(contractAnalysisType, { status: 201 })
    }
  ),
  http.put<
    { contractId: string; id: string },
    UpdateContractAnalysisType,
    ContractAnalysisType
  >(
    '/api/contracts/:contractId/analysis-types/:id',
    async ({ params, request }) => {
      const contract = mockData.contracts.find(
        (contract) => contract.id.toString() === params.contractId
      )
      const analysisTypeIndex = contract?.analysisTypes?.findIndex(
        (analysisType) => analysisType.id.toString() === params.id
      )
      if (
        !contract?.analysisTypes ||
        analysisTypeIndex == null ||
        analysisTypeIndex < 0
      ) {
        return notFound()
      }
      const body = await request.json()
      const updated = {
        ...contract.analysisTypes[analysisTypeIndex],
        ...body,
        updatedTime: new Date().toISOString(),
        updatedBy: 'Testbruker 1'
      }
      contract.analysisTypes[analysisTypeIndex] = updated
      return HttpResponse.json(updated)
    }
  ),
  http.delete<{ contractId: string; id: string }>(
    '/api/contracts/:contractId/analysis-types/:id',
    async ({ params }) => {
      const contract = mockData.contracts.find(
        (contract) => contract.id.toString() === params.contractId
      )
      if (!contract) return notFound()
      contract.analysisTypes = contract.analysisTypes?.filter(
        (analysisType) => analysisType.id.toString() !== params.id
      )
      return noContent()
    }
  ),
  http.post<{ contractId: string; id: string }>(
    '/api/contracts/:contractId/analysis-types/:id/approve',
    async ({ params }) => {
      const contract = mockData.contracts.find(
        (contract) => contract.id.toString() === params.contractId
      )
      const analysisType = contract?.analysisTypes?.find(
        (analysisType) => analysisType.id.toString() === params.id
      )
      if (!analysisType) return notFound()
      analysisType.mtApproval = true
      return HttpResponse.json(analysisType)
    }
  ),
  http.post<{ contractId: string; id: string }>(
    '/api/contracts/:contractId/analysis-types/:id/reject',
    async ({ params, request }) => {
      const contract = mockData.contracts.find(
        (contract) => contract.id.toString() === params.contractId
      )
      const analysisType = contract?.analysisTypes?.find(
        (analysisType) => analysisType.id.toString() === params.id
      )
      if (!analysisType) return notFound()
      const body = (await request.json()) as { reason?: string }
      analysisType.mtApproval = false
      analysisType.approvalComment = body.reason
      return HttpResponse.json(analysisType)
    }
  ),
  http.get('/api/important-messages', async ({ request }) => {
    const url = new URL(request.url)
    const activeNow = url.searchParams.get('activeNow')
    const messages =
      activeNow === 'true'
        ? mockData.importantMessages.filter((message) => isActiveNow(message))
        : mockData.importantMessages
    return HttpResponse.json(messages)
  }),
  http.post<never, ImportantMessageInput, ImportantMessage>(
    '/api/important-messages',
    async ({ request }) => {
      const body = await request.json()
      const now = new Date().toISOString()
      const message: ImportantMessage = {
        id: createMockId(),
        title: body.title,
        text: body.text,
        startDate: body.startDate,
        endDate: body.endDate,
        createdBy: '1234567890',
        createdAt: now,
        updatedAt: now,
        etag: newEtag()
      }
      mockData.importantMessages.push(message)
      return HttpResponse.json(message, { status: 201 })
    }
  ),
  http.put<{ messageId: string }, ImportantMessageInput, ImportantMessage>(
    '/api/important-messages/:messageId',
    async ({ params, request }) => {
      const index = mockData.importantMessages.findIndex(
        (message) => message.id.toString() === params.messageId
      )
      if (index === -1) return notFound()
      const ifMatch = request.headers.get('If-Match')
      if (ifMatch !== mockData.importantMessages[index].etag) {
        return new HttpResponse(null, { status: 412 })
      }
      const body = await request.json()
      const updated = {
        ...mockData.importantMessages[index],
        ...body,
        updatedAt: new Date().toISOString(),
        etag: newEtag()
      }
      mockData.importantMessages[index] = updated
      return HttpResponse.json(updated)
    }
  ),
  http.delete<{ messageId: string }>(
    '/api/important-messages/:messageId',
    async ({ params, request }) => {
      const message = mockData.importantMessages.find(
        (message) => message.id.toString() === params.messageId
      )
      if (!message) return notFound()
      const ifMatch = request.headers.get('If-Match')
      if (ifMatch !== message.etag) {
        return new HttpResponse(null, { status: 412 })
      }
      mockData.importantMessages = mockData.importantMessages.filter(
        (message) => message.id.toString() !== params.messageId
      )
      return noContent()
    }
  ),
  http.get<{
    planId: string
  }>('/api/plans/:planId/sample-summary', async ({ params }) => {
    const plan = mockData.plans.plans.find(
      (plan) => plan.id.toString() === params.planId
    )
    if (plan == null) return notFound()

    const taskIds = new Set(plan.tasks?.map((task) => task.id) ?? [])
    const samples = mockData.samples.filter(
      (sample) => sample.taskId != null && taskIds.has(sample.taskId)
    )
    const positiveSampleCount = samples.filter((sample) =>
      sample.requisitions?.some(
        (requisition) => requisition.results?.status === 'POSITIVE'
      )
    ).length
    const negativeSampleCount = samples.filter((sample) =>
      sample.requisitions?.some(
        (requisition) => requisition.results?.status === 'NEGATIVE'
      )
    ).length

    return HttpResponse.json({
      totalSampleCount: samples.length,
      positiveSampleCount,
      negativeSampleCount,
      undeterminedSampleCount:
        samples.length - positiveSampleCount - negativeSampleCount
    })
  }),
  http.get<{
    planId: string
  }>('/api/plans/:planId', async ({ params }) => {
    const { planId } = params
    const plan = mockData.plans.plans.find(
      (plan) => plan.id.toString() === planId
    )
    if (plan == null) {
      return notFound()
    }
    return HttpResponse.json({
      ...toSinglePlan(plan),
      canManage: true,
      canCreateChildren: true
    })
  }),
  http.put<{ planId: string }, UpdatePlan, SinglePlan>(
    '/api/plans/:planId',
    async ({ params, request }) => {
      const planIndex = mockData.plans.plans.findIndex(
        (plan) => plan.id.toString() === params.planId
      )
      if (planIndex === -1) return notFound()
      if (
        request.headers.get('If-Match') !== mockData.plans.plans[planIndex].etag
      ) {
        return new HttpResponse(null, { status: 412 })
      }
      const body = await request.json()
      const currentPlan = mockData.plans.plans[planIndex]
      const instructions = {
        samplingInstructions:
          body.samplingInstructions === ''
            ? null
            : (body.samplingInstructions ??
              currentPlan.samplingInstructions ??
              null),
        storageInstructions:
          body.storageInstructions === ''
            ? null
            : (body.storageInstructions ??
              currentPlan.storageInstructions ??
              null),
        labelingInstructions:
          body.labelingInstructions === ''
            ? null
            : (body.labelingInstructions ??
              currentPlan.labelingInstructions ??
              null),
        shippingInstructions:
          body.shippingInstructions === ''
            ? null
            : (body.shippingInstructions ??
              currentPlan.shippingInstructions ??
              null),
        followUpInstructions:
          body.followUpInstructions === ''
            ? null
            : (body.followUpInstructions ??
              currentPlan.followUpInstructions ??
              null)
      }
      const parent = mockData.plans.plans.find(
        (plan) => plan.id === currentPlan.parents?.[0]
      )
      const directDue = body.due ?? null
      const directContractRef = body.contractRef ?? null
      const directWorkspaceId = body.workspaceId ?? null
      const directPurposeId = body.purposeId ?? null
      const updated: MockCompactPlan = {
        ...currentPlan,
        ...body,
        directDue,
        directContractRef,
        directWorkspaceId,
        directPurposeId,
        due: directDue ?? parent?.due,
        contractRef: directContractRef ?? parent?.contractRef,
        workspaceId: directWorkspaceId ?? parent?.workspaceId,
        fagseksjonWorkspaceId:
          body.fagseksjonWorkspaceId ?? currentPlan.fagseksjonWorkspaceId,
        effectiveFagseksjonWorkspaceId:
          body.fagseksjonWorkspaceId ??
          currentPlan.fagseksjonWorkspaceId ??
          parent?.effectiveFagseksjonWorkspaceId ??
          parent?.fagseksjonWorkspaceId,
        purposeId: directPurposeId ?? parent?.purposeId,
        ...instructions,
        contractId: body.contractId ?? undefined,
        okProgramId: body.okProgramId ?? undefined,
        etag: newEtag()
      }
      mockData.plans.plans[planIndex] = updated
      return HttpResponse.json(toSinglePlan(updated as MockCompactPlan))
    }
  ),
  http.delete<{
    planId: string
  }>('/api/plans/:planId', async ({ params }) => {
    const { planId } = params
    mockData.plans.plans = mockData.plans.plans.filter(
      (plan) => plan.id.toString() !== planId
    )
    return noContent()
  }),
  http.post<
    { planId: string },
    { newDescription?: string; ownerId?: string },
    SinglePlan
  >('/api/plans/copy/:planId', async ({ params, request }) => {
    const source = mockData.plans.plans.find(
      (plan) => plan.id.toString() === params.planId
    )
    if (!source) return notFound()
    const body = await request.json()
    const copy: CompactPlan = {
      ...structuredClone(source),
      id: createMockId(),
      ownerId: body.ownerId ?? source.ownerId,
      description:
        body.newDescription ?? `${source.description ?? 'Plan'} kopi`,
      parents: [],
      children: [],
      etag: newEtag()
    }
    mockData.plans.plans.push(copy)
    return HttpResponse.json(toSinglePlan(copy), { status: 201 })
  }),
  http.post<{ planId: string }, { taskId: number }>(
    '/api/plans/:planId/tasks',
    async ({ params, request }) => {
      const plan = mockData.plans.plans.find(
        (plan) => plan.id.toString() === params.planId
      )
      const body = await request.json()
      const task = mockData.tasks.find((task) => task.id === body.taskId)
      if (!plan || !task) return notFound()
      task.planIds = Array.from(new Set([...(task.planIds ?? []), plan.id]))
      replacePlanTask(task)
      return HttpResponse.json(toGroupedTask(task), { status: 201 })
    }
  ),
  http.get<{ taskId: string }>(
    '/api/tasks/:taskId/move-destinations',
    ({ params }) => {
      const task = mockData.tasks.find(
        (entry) => entry.id === Number(params.taskId)
      )
      if (!task) return notFound()
      return HttpResponse.json(
        mockData.plans.plans
          .filter((plan) => !task.planIds?.includes(plan.id))
          .map((plan) => plan.id)
      )
    }
  ),
  http.get<{ planId: string }>(
    '/api/plans/:planId/move-destinations',
    ({ params }) => {
      const plan = mockData.plans.plans.find(
        (entry) => entry.id === Number(params.planId)
      )
      if (!plan) return notFound()
      if (!plan.parents?.length) return new HttpResponse(null, { status: 400 })
      const excluded = new Set<number>([plan.id, ...plan.parents])
      const descendants = new Set<number>([plan.id])
      let changed = true
      while (changed) {
        changed = false
        for (const candidate of mockData.plans.plans) {
          if (
            !descendants.has(candidate.id) &&
            candidate.parents?.some((parent) => descendants.has(parent))
          ) {
            descendants.add(candidate.id)
            changed = true
          }
        }
      }
      return HttpResponse.json(
        mockData.plans.plans
          .filter(
            (entry) => !excluded.has(entry.id) && !descendants.has(entry.id)
          )
          .map((entry) => entry.id)
      )
    }
  ),
  http.post<
    { planId: string },
    { sourceParentId: number; destinationPlanId: number }
  >('/api/plans/:planId/move', async ({ params, request }) => {
    const plan = mockData.plans.plans.find(
      (entry) => entry.id === Number(params.planId)
    )
    const body = await request.json()
    const destination = mockData.plans.plans.find(
      (entry) => entry.id === body.destinationPlanId
    )
    if (!plan || !destination) return notFound()
    if (request.headers.get('If-Match')?.replaceAll('"', '') !== plan.etag)
      return new HttpResponse(null, { status: 412 })
    if (plan.parents?.[0] !== body.sourceParentId)
      return new HttpResponse(null, { status: 409 })
    const old = mockData.plans.plans.find(
      (entry) => entry.id === body.sourceParentId
    )
    if (old) old.children = old.children?.filter((id) => id !== plan.id)
    plan.parents = [destination.id]
    destination.children = [...(destination.children ?? []), plan.id]
    plan.etag = newEtag()
    return new HttpResponse(null, {
      status: 200,
      headers: { ETag: `"${plan.etag}"` }
    })
  }),
  http.post<{ planId: string; taskId: string }, { destinationPlanId: number }>(
    '/api/plans/:planId/tasks/:taskId/move',
    async ({ params, request }) => {
      const source = mockData.plans.plans.find(
        (plan) => plan.id === Number(params.planId)
      )
      const task = mockData.tasks.find(
        (task) => task.id === Number(params.taskId)
      )
      const { destinationPlanId } = await request.json()
      const destination = mockData.plans.plans.find(
        (plan) => plan.id === destinationPlanId
      )
      if (!source || !destination || !task) return notFound()
      if (request.headers.get('If-Match')?.replaceAll('"', '') !== task.etag)
        return new HttpResponse(null, { status: 412 })
      if (source.id === destination.id)
        return new HttpResponse(null, { status: 400 })
      if (
        !task.planIds?.includes(source.id) ||
        task.planIds.includes(destination.id)
      )
        return new HttpResponse(null, { status: 409 })
      task.planIds = [
        ...task.planIds.filter((id) => id !== source.id),
        destination.id
      ]
      task.etag = newEtag()
      source.tasks = source.tasks?.filter((entry) => entry.id !== task.id)
      replacePlanTask(task)
      return new HttpResponse(null, {
        status: 200,
        headers: { ETag: `"${task.etag}"` }
      })
    }
  ),
  http.delete<{ planId: string; taskId: string }>(
    '/api/plans/:planId/tasks/:taskId',
    async ({ params }) => {
      const plan = mockData.plans.plans.find(
        (plan) => plan.id.toString() === params.planId
      )
      if (!plan) return notFound()
      plan.tasks = plan.tasks?.filter(
        (task) => task.id.toString() !== params.taskId
      )
      const task = mockData.tasks.find(
        (task) => task.id.toString() === params.taskId
      )
      if (task) task.planIds = task.planIds?.filter((id) => id !== plan.id)
      return noContent()
    }
  ),

  // tasks

  http.get('/api/tasks', async ({ request }) => {
    const url = new URL(request.url)
    const ownerIds = url.searchParams.getAll('ownerId')
    const taskIds = url.searchParams.getAll('taskIds')
    let tasks = mockData.tasks
    if (ownerIds.length > 0) {
      tasks = tasks.filter((task) => ownerIds.includes(task.ownerId))
    }
    if (taskIds.length > 0) {
      tasks = tasks.filter((task) => taskIds.includes(task.id.toString()))
    }
    return HttpResponse.json(tasks)
  }),
  http.get<{
    taskId: string
  }>('/api/tasks/:taskId/sample-summary', async ({ params }) => {
    const task = mockData.tasks.find(
      (task) => task.id.toString() === params.taskId
    )
    if (task == null) return notFound()

    const samples = mockData.samples.filter(
      (sample) => sample.taskId === task.id
    )
    const positiveSampleCount = samples.filter((sample) =>
      sample.requisitions?.some(
        (requisition) => requisition.results?.status === 'POSITIVE'
      )
    ).length
    const negativeSampleCount = samples.filter((sample) =>
      sample.requisitions?.some(
        (requisition) => requisition.results?.status === 'NEGATIVE'
      )
    ).length

    return HttpResponse.json({
      totalSampleCount: samples.length,
      positiveSampleCount,
      negativeSampleCount,
      undeterminedSampleCount:
        samples.length - positiveSampleCount - negativeSampleCount
    })
  }),
  http.get<{
    taskId: string
  }>('/api/tasks/:taskId', async ({ params }) => {
    const { taskId } = params
    const task = mockData.tasks.find((task) => task.id.toString() === taskId)
    if (task == null) {
      return notFound()
    }
    return HttpResponse.json({
      ...task,
      canManage: true
    })
  }),
  http.get<{ taskId: string }>(
    '/api/tasks/:taskId/delegation',
    async ({ params }) => {
      const delegation = mockData.taskDelegations[Number(params.taskId)]
      if (delegation == null) return notFound()
      return HttpResponse.json(delegation, {
        headers: {
          ETag:
            mockData.tasks.find((task) => task.id === delegation.taskId)
              ?.etag ?? ''
        }
      })
    }
  ),
  http.put<{ taskId: string }, ReplaceTaskDelegation, TaskDelegation>(
    '/api/tasks/:taskId/delegation',
    async ({ params, request }) => {
      const taskId = Number(params.taskId)
      const current = mockData.taskDelegations[taskId]
      const task = mockData.tasks.find((candidate) => candidate.id === taskId)
      if (current == null) return notFound()
      if (
        task == null ||
        request.headers.get('If-Match')?.replaceAll('"', '') !== task.etag
      ) {
        return HttpResponse.json(null, { status: 412 })
      }
      const body = await request.json()
      const paths = new Set(body.entries.map((entry) => entry.path.join('/')))
      const isInvalid = body.entries.some(
        (entry) =>
          entry.plannedQuantity < 0 ||
          (entry.path.length > 1 &&
            !paths.has(entry.path.slice(0, -1).join('/')))
      )
      if (isInvalid) return HttpResponse.json(null, { status: 400 })

      const previousByPath = new Map(
        current.entries.map((entry) => [entry.path.join('/'), entry])
      )
      const etag = newEtag()
      const entries = body.entries.map((entry, index) => {
        const key = entry.path.join('/')
        const delegated = body.entries
          .filter(
            (candidate) =>
              candidate.path.length === entry.path.length + 1 &&
              candidate.path.slice(0, -1).join('/') === key
          )
          .reduce((sum, child) => sum + child.plannedQuantity, 0)
        const previous = previousByPath.get(key)
        return {
          taskDelegationId:
            previous?.taskDelegationId ?? createMockId() + index,
          path: entry.path,
          ownerId: entry.path.at(-1)!,
          plannedQuantity: entry.plannedQuantity,
          delegatedQuantity: delegated,
          retainedQuantity: entry.plannedQuantity - delegated,
          samplesTaken: previous?.samplesTaken ?? 0,
          isLeaf: delegated === 0
        }
      })
      if (entries.some((entry) => entry.retainedQuantity < 0)) {
        return HttpResponse.json(null, { status: 400 })
      }
      const delegation: TaskDelegation = {
        ...current,
        entries
      }
      task.etag = etag
      mockData.taskDelegations[taskId] = delegation
      return HttpResponse.json(delegation, { headers: { ETag: etag } })
    }
  ),
  http.get<{ taskId: string }>(
    '/api/tasks/:taskId/delegation-audit',
    async () => HttpResponse.json(mockData.taskDelegationAudits)
  ),
  http.get('/api/labs/:labId/reception-addresses', () => HttpResponse.json([])),
  http.post<never, CreateTask, Task>('/api/tasks', async ({ request }) => {
    const createTask: CreateTask = await request.json()
    const task = buildTaskFromPayload(
      createTask,
      createMockId(),
      createTask.planIds ?? []
    )
    if (task == null) {
      return HttpResponse.json(null, {
        status: 400,
        headers: { 'Content-Length': '0' }
      })
    }
    mockData.tasks.push(task)
    replacePlanTask(task)
    return HttpResponse.json(task, { status: 201 })
  }),
  http.put<{ taskId: string }, UpdateTask, Task>(
    '/api/tasks/:taskId',
    async ({ params, request }) => {
      const index = mockData.tasks.findIndex(
        (task) => task.id.toString() === params.taskId
      )
      if (index === -1) return notFound()
      const body = await request.json()
      const task = buildTaskFromPayload(
        body,
        mockData.tasks[index].id,
        mockData.tasks[index].planIds ?? []
      )
      if (!task) return notFound()
      mockData.tasks[index] = task
      replacePlanTask(task)
      return HttpResponse.json(task)
    }
  ),
  http.delete<{
    taskId: string
  }>('/api/tasks/:taskId', async ({ params }) => {
    const { taskId } = params
    const target = mockData.tasks.find((task) => task.id.toString() === taskId)
    // Speiler backend: en fordeling som allerede er borte gir 404, og
    // task_delegation.parent_id har ON DELETE CASCADE, så hele grenen følger med.
    if (target == null) {
      return notFound()
    }
    const removedIds = new Set<number>()
    const collect = (task: Task) => {
      removedIds.add(task.id)
      mockData.tasks
        .filter((subTask) => subTask.parentTaskId === task.id)
        .forEach(collect)
    }
    collect(target)
    mockData.tasks = mockData.tasks.filter((task) => !removedIds.has(task.id))
    return noContent()
  }),

  // samples
  http.get('/api/samples', async ({ request }) => {
    const url = new URL(request.url)
    const samples = filterSamples(mockData.samples, url)
    const page = Number(url.searchParams.get('page') ?? 0)
    const size = Number(url.searchParams.get('size') ?? 20)
    const start = page * size
    const content = samples.slice(start, start + size)

    const samplePage = {
      content,
      numberOfElements: content.length,
      totalElements: samples.length,
      size,
      number: page,
      totalPages: Math.max(1, Math.ceil(samples.length / size))
    }

    return HttpResponse.json(samplePage)
  }),
  http.get('/api/samples/:sampleId', async ({ params }) => {
    const { sampleId } = params
    if (typeof sampleId === 'string') {
      const sample = mockData.samples.find((sample) => sample.id === sampleId)
      if (sample == null) {
        return notFound()
      }
      return HttpResponse.json(sample)
    }
  }),
  http.get('/api/samples/:sampleId/history', async ({ params }) => {
    const { sampleId } = params
    if (typeof sampleId === 'string') {
      const history = mockData.sampleHistories[sampleId]
      if (history == null) {
        return notFound()
      }
      return HttpResponse.json(history)
    }
  }),
  http.put<{ sampleId: string }, CreateSample, Sample>(
    '/api/samples/:sampleId',
    async ({ params, request }) => {
      const { sampleId } = params
      const createSample: CreateSample = await request.json()
      const isSampleRegistered = mockData.samples.some(
        (sample) => sample.id === sampleId
      )
      if (isSampleRegistered) {
        return HttpResponse.json(null, {
          status: 409,
          headers: { 'Content-Length': '0' }
        })
      }
      const sampleType = mockData.sampleTypes.find(
        (sampleType) => sampleType.id === createSample.sampleTypeId
      )
      const sampntType = mockData.sampntTypes.find(
        (sampntType) => sampntType.id === createSample.sampntTypeId
      )
      if (sampleType != null && sampntType != null) {
        const sample: Sample = {
          id: sampleId,
          sampleType,
          etag: 'etag',
          takenDate: createSample.takenDate,
          planId: createSample.planId,
          taskId: createSample.taskId,
          comment: createSample.comment,
          geometry: createSample.geometry,
          locationId: createSample.locationId,
          oversightObject: createSample.oversightObject,
          externalReferenceId: createSample.externalReferenceId,
          subSamples: createSample.subSamples?.map((subSample) => ({
            ...subSample,
            id: createMockId()
          })),
          nameRedacted: createSample.nameRedacted,
          accuratePosition: createSample.accuratePosition ?? false,
          sampntType
        }
        mockData.samples.push(sample)
        return HttpResponse.json(sample)
      }
    }
  ),
  http.patch<{ sampleId: string }, UpdateSample, Sample>(
    '/api/samples/:sampleId',
    async ({ params, request }) => {
      const { sampleId } = params
      const sample = mockData.samples.find((sample) => sample.id === sampleId)
      if (sample == null) {
        return notFound()
      }
      const updateSample: UpdateSample = await request.json()
      Object.assign(sample, updateSample)
      return HttpResponse.json(sample)
    }
  ),
  http.get(
    '/api/samples/:sampleId/inspector-candidates',
    async ({ params }) => {
      const { sampleId } = params
      if (typeof sampleId !== 'string') return notFound()
      const sample = mockData.samples.find((s) => s.id === sampleId)
      if (!sample) return notFound()

      const candidates = [
        {
          userId: 'user:kari-nordmann',
          name: 'Kari Nordmann',
          email: 'kari.nordmann@mattilsynet.no',
          employeeId: 'EMP001',
          tier: 'TEAM' as const,
          currentInspector: sample.userId === 'user:kari-nordmann',
          team: 'Team Fisk'
        },
        {
          userId: 'user:ola-nordmann',
          name: 'Ola Nordmann',
          email: 'ola.nordmann@mattilsynet.no',
          employeeId: 'EMP002',
          tier: 'TEAM' as const,
          currentInspector: sample.userId === 'user:ola-nordmann',
          team: 'Team Fisk'
        },
        {
          userId: 'user:per-hansen',
          name: 'Per Hansen',
          email: 'per.hansen@mattilsynet.no',
          employeeId: 'EMP003',
          tier: 'SECTION' as const,
          currentInspector: sample.userId === 'user:per-hansen',
          section: 'Seksjon Kjøtt'
        },
        {
          userId: 'user:berit-olsen',
          name: 'Berit Olsen',
          email: 'berit.olsen@mattilsynet.no',
          employeeId: 'EMP004',
          tier: 'DEPARTMENT' as const,
          currentInspector: sample.userId === 'user:berit-olsen',
          department: 'Avdeling Oslo'
        },
        {
          userId: 'user:astrid-lind',
          name: 'Astrid Lind',
          email: 'astrid.lind@mattilsynet.no',
          employeeId: 'EMP005',
          tier: 'REGION' as const,
          currentInspector: sample.userId === 'user:astrid-lind'
        },
        {
          userId: 'user:nils-utenfor',
          name: 'Nils Utenfor',
          email: 'nils.utenfor@mattilsynet.no',
          employeeId: 'EMP006',
          tier: 'OTHER' as const,
          currentInspector: sample.userId === 'user:nils-utenfor'
        },
        {
          userId: 'user:kun-epost',
          email: 'kun.epost@mattilsynet.no',
          tier: 'OTHER' as const,
          currentInspector: sample.userId === 'user:kun-epost'
        },
        {
          userId: 'user:uten-navn-epost',
          tier: 'OTHER' as const,
          currentInspector: sample.userId === 'user:uten-navn-epost'
        }
      ]

      const currentInspector =
        candidates.find((c) => c.currentInspector) ??
        (sample.userId
          ? {
              userId: sample.userId,
              tier: 'OTHER' as const,
              currentInspector: true
            }
          : undefined)
      return HttpResponse.json({
        sampleId,
        currentInspector,
        anchor: {
          id: 'a1',
          type: 'AVDELING',
          name: 'Avdeling Stor-Oslo'
        },
        rankingCompleteness: 'COMPLETE',
        candidates
      })
    }
  ),
  http.patch(
    '/api/samples/:sampleId/inspector',
    async ({ params, request }) => {
      const { sampleId } = params
      if (typeof sampleId !== 'string') return notFound()
      const sample = mockData.samples.find((s) => s.id === sampleId)
      if (!sample) return notFound()

      const ifMatch = request.headers.get('If-Match')
      if (!ifMatch || ifMatch !== sample.etag) {
        return HttpResponse.json(
          {
            type: 'about:blank',
            title: 'Precondition Failed',
            status: 412,
            detail: 'Precondition Failed - ETag does not match'
          },
          {
            status: 412,
            headers: { 'Content-Type': 'application/problem+json' }
          }
        )
      }

      const body = (await request.json()) as { userId: string }
      sample.userId = body.userId
      sample.etag = `etag-${Date.now()}`

      const candidateLookup: Record<string, { name?: string; email?: string }> =
        {
          'user:kari-nordmann': {
            name: 'Kari Nordmann',
            email: 'kari.nordmann@mattilsynet.no'
          },
          'user:ola-nordmann': {
            name: 'Ola Nordmann',
            email: 'ola.nordmann@mattilsynet.no'
          },
          'user:per-hansen': {
            name: 'Per Hansen',
            email: 'per.hansen@mattilsynet.no'
          },
          'user:berit-olsen': {
            name: 'Berit Olsen',
            email: 'berit.olsen@mattilsynet.no'
          },
          'user:astrid-lind': {
            name: 'Astrid Lind',
            email: 'astrid.lind@mattilsynet.no'
          },
          'user:nils-utenfor': {
            name: 'Nils Utenfor',
            email: 'nils.utenfor@mattilsynet.no'
          },
          'user:kun-epost': {
            email: 'kun.epost@mattilsynet.no'
          }
        }

      const userDetails = candidateLookup[body.userId]

      return HttpResponse.json(
        {
          sampleId,
          userId: body.userId,
          etag: sample.etag,
          name: userDetails?.name,
          email: userDetails?.email
        },
        {
          headers: {
            ETag: sample.etag
          }
        }
      )
    }
  ),

  http.delete('/api/samples/:sampleId', async ({ params }) => {
    const { sampleId } = params
    if (typeof sampleId === 'string') {
      mockData.samples = mockData.samples.filter(
        (sample) => sample.id !== sampleId
      )
      return noContent()
    }
  }),
  // analysis-types
  http.post<never, CreateAnalysisType, AnalysisType>(
    '/api/analysis-types',
    async ({ request }) => {
      const createAnalysisType: CreateAnalysisType = await request.json()
      const createAnalysisTypeSubstances:
        CreateAnalysisTypeSubstance[] | undefined =
        createAnalysisType.substances
      const sampleType = mockData.sampleTypes.find(
        (sampleType) => sampleType.id === createAnalysisType.sampleTypeId
      )
      const analysisTypeId = createMockId()
      const analysisTypeSubstances: AnalysisTypeSubstance[] = []
      createAnalysisTypeSubstances?.forEach((createAnalysisTypeSubstance) => {
        const substance = mockData.substances.find(
          (substance) =>
            substance.id === createAnalysisTypeSubstance.substanceId
        )
        if (substance != null) {
          analysisTypeSubstances.push({
            id: createMockId(),
            analysisTypeId,
            substance
          })
        }
      })
      const payloadSchema = mockData.payloadSchemas.find(
        (payloadSchema) =>
          payloadSchema.id === createAnalysisType.payloadSchemaId
      )
      const analysisType: AnalysisType = {
        id: analysisTypeId,
        sampleType,
        substances: analysisTypeSubstances,
        payloadSchema
      }
      mockData.analysisTypes.push(analysisType)
      return HttpResponse.json(analysisType)
    }
  ),
  http.get('/api/analysis-types', async () => {
    return HttpResponse.json(mockData.analysisTypes)
  }),
  http.get('/api/analysis-types/:analysisTypeId', async ({ params }) => {
    const { analysisTypeId } = params
    if (typeof analysisTypeId === 'string') {
      const analysisType = mockData.analysisTypes.find(
        (analysisType) => analysisType.id.toString() === analysisTypeId
      )
      if (analysisType == null) {
        return notFound()
      }
      return HttpResponse.json(analysisType)
    }
  }),
  http.delete('/api/analysis-types/:analysisTypeId', async ({ params }) => {
    const { analysisTypeId } = params
    if (typeof analysisTypeId === 'string') {
      mockData.analysisTypes = mockData.analysisTypes.filter(
        (analysisType) => analysisType.id.toString() !== analysisTypeId
      )
      return noContent()
    }
  }),

  // sample-types
  http.post<never, CreateSampleType, SampleType>(
    '/api/sample-types',
    async ({ request }) => {
      const createSampleType: CreateSampleType = await request.json()
      const matrix: Matrix | undefined = mockData.matrices.find(
        (matrix) => matrix.id === createSampleType.matrixId
      )
      const product: Product | undefined = mockData.products.find(
        (product) => product.id === createSampleType.productId
      )
      if (matrix != null && product != null) {
        const sampleType: SampleType = {
          id: createMockId(),
          matrixId: createSampleType.matrixId,
          matrix: matrix.name,
          matrixCode: matrix.code,
          productId: createSampleType.productId,
          product: product.name,
          productCode: product.code,
          description: createSampleType.description
        }
        mockData.sampleTypes.push(sampleType)
        return HttpResponse.json(sampleType)
      }
    }
  ),
  http.get('/api/sample-types', async () => {
    return HttpResponse.json(mockData.sampleTypes)
  }),
  http.get('/api/sample-types/:sampleTypeId', async ({ params }) => {
    const { sampleTypeId } = params
    if (typeof sampleTypeId === 'string') {
      const sampleType = mockData.sampleTypes.find(
        (sampleType) => sampleType.id.toString() === sampleTypeId
      )
      if (sampleType == null) {
        return notFound()
      }
      return HttpResponse.json(sampleType)
    }
  }),
  http.delete('/api/sample-types/:sampleTypeId', async ({ params }) => {
    const { sampleTypeId } = params
    if (typeof sampleTypeId === 'string') {
      mockData.sampleTypes = mockData.sampleTypes.filter(
        (sampleType) => sampleType.id.toString() !== sampleTypeId
      )
      return noContent()
    }
  }),

  // requisitions
  http.post<never, CreateRequisition, Requisition>(
    '/api/requisitions',
    async ({ request }) => {
      const createRequisition: CreateRequisition = await request.json()
      const analysisType = mockData.analysisTypes.find(
        (analysisType) => analysisType.id === createRequisition.analysisTypeId
      )
      const sample = mockData.samples.find(
        (sample) => sample.id === createRequisition.sampleId
      )
      const lab = mockData.labs.find(
        (lab) => lab.id === createRequisition.labId
      )
      if (analysisType != null && sample != null && lab != null) {
        const requisition: Requisition = {
          id: createMockId(),
          sample,
          lab,
          analysisType,
          payload: createRequisition.payload,
          createdDate: new Date().toISOString(),
          etag: 'etag'
        }
        mockData.requisitions.push(requisition)
        return HttpResponse.json(requisition)
      }
    }
  ),
  http.post<{ requisitionId: string }>(
    '/api/requisitions/:requisitionId/reset-processing',
    async ({ params, request }) => {
      const requisition = mockData.requisitions.find(
        (item) => item.id.toString() === params.requisitionId
      )
      if (!requisition) return notFound()
      if (
        request.headers.get('If-Match')?.replaceAll('"', '') !==
        requisition.etag
      )
        return new HttpResponse(null, { status: 412 })
      requisition.externalCreatedDate = undefined
      requisition.processed = false
      requisition.processingResetEligible = false
      requisition.etag = newEtag()
      if (requisition.sample) requisition.sample.etag = newEtag()
      return HttpResponse.json(requisition, {
        headers: { ETag: `"${requisition.etag}"` }
      })
    }
  ),
  http.get('/api/requisitions', async ({ request }) => {
    const url = new URL(request.url)
    const sampleId: string | null = url.searchParams.get('sampleId')
    if (sampleId == null) {
      return HttpResponse.json(mockData.requisitions)
    }
    return HttpResponse.json(
      mockData.requisitions.filter(
        (requisition) => requisition.sample?.id === sampleId
      )
    )
  }),
  http.get<{
    requisitionId: string
  }>('/api/requisitions/:requisitionId', async ({ params }) => {
    const { requisitionId } = params
    const requisition = mockData.requisitions.find(
      (requisition) => requisition.id.toString() === requisitionId
    )
    if (requisition == null) {
      return notFound()
    }
    return HttpResponse.json(requisition)
  }),
  http.delete<{
    requisitionId: string
  }>('/api/requisitions/:requisitionId', async ({ params }) => {
    const { requisitionId } = params
    mockData.requisitions = mockData.requisitions.filter(
      (requisition) => requisition.id.toString() !== requisitionId
    )
    return noContent()
  }),

  // results
  http.post(
    '/api/requisitions/:requisitionId/results/:resultId/attachments',
    async () => {
      return HttpResponse.json(
        {
          signedUrl: 'https://example.test/upload',
          expiration: new Date(Date.now() + 60 * 60 * 1000).toISOString()
        },
        { status: 201 }
      )
    }
  ),
  http.post('/api/results', async ({ request }) => {
    const rawBody = await request.clone().text()
    const filenames = Array.from(rawBody.matchAll(/filename="([^"]+)"/g)).map(
      (match) => match[1]
    )
    const formData = await request.formData()
    const files = formData.getAll('files') as File[]
    return HttpResponse.json({
      statuses: files.map((file, index) => ({
        filename: filenames[index] ?? file.name,
        status: 'OK'
      }))
    })
  }),
  http.get<{
    requisitionId: string
  }>('/api/requisitions/:requisitionId/results', async ({ params }) => {
    const { requisitionId } = params
    const requisition = mockData.requisitions.find(
      (requisition) => requisition.id.toString() === requisitionId
    )
    if (requisition == null) {
      return notFound()
    }
    const results: RequisitionResult | undefined = requisition.results
    return HttpResponse.json(results)
  }),
  http.post<{ requisitionId: string }>(
    '/api/requisitions/:requisitionId/results',
    async ({ params, request }) => {
      const { requisitionId } = params
      const requisition = mockData.requisitions.find(
        (requisition) => requisition.id.toString() === requisitionId
      )
      if (requisition == null) {
        return notFound()
      }
      const body = (await request.json()) as Partial<RequisitionResult>
      requisition.results = {
        ...requisition.results,
        ...body
      }
      return HttpResponse.json(requisition.results, { status: 201 })
    }
  ),
  http.put<{ requisitionId: string; resultId: string }>(
    '/api/requisitions/:requisitionId/results/:resultId',
    async ({ params, request }) => {
      const { requisitionId, resultId } = params
      const requisition = mockData.requisitions.find(
        (requisition) => requisition.id.toString() === requisitionId
      )
      if (requisition?.results == null) {
        return notFound()
      }
      const body = (await request.json()) as {
        status?: { status?: string }
      }
      const subSamples = requisition.results.subSamples ?? {}
      const resultSet = Object.values(subSamples)
        .flatMap((result) => result.resultSets ?? [])
        .find((resultSet) => resultSet.resultId.toString() === resultId)
      if (resultSet == null) {
        return notFound()
      }
      resultSet.status = body.status?.status as typeof resultSet.status
      return HttpResponse.json(resultSet)
    }
  ),

  // labs
  http.post<never, CreateLab, Lab>('/api/labs', async ({ request }) => {
    const createLab: CreateLab = await request.json()
    const lab: Lab = {
      id: createMockId(),
      name: createLab.name,
      info: createLab.info
    }
    mockData.labs.push(lab)
    return HttpResponse.json(lab)
  }),
  http.get('/api/labs', async () => {
    return HttpResponse.json(mockData.labs)
  }),
  http.get<{
    labId: string
  }>('/api/labs/:labId', async ({ params }) => {
    const { labId } = params
    const lab = mockData.labs.find((lab) => lab.id.toString() === labId)
    if (lab == null) {
      return notFound()
    }
    return HttpResponse.json(lab)
  }),
  http.delete<{
    labId: string
  }>('/api/labs/:labId', async ({ params }) => {
    const { labId } = params
    mockData.labs = mockData.labs.filter((lab) => lab.id.toString() !== labId)
    return noContent()
  }),
  // matrices
  http.post<never, CreateMatrix, Matrix>(
    '/api/matrices',
    async ({ request }) => {
      const createMatrix: CreateMatrix = await request.json()
      const matrix: Matrix = {
        id: createMockId(),
        name: createMatrix.name,
        code: createMatrix.code
      }
      mockData.matrices.push(matrix)
      return HttpResponse.json(matrix)
    }
  ),
  http.get('/api/matrices', async () => {
    return HttpResponse.json(mockData.matrices)
  }),
  http.get<{
    matrixId: string
  }>('/api/matrices/:matrixId', async ({ params }) => {
    const { matrixId } = params
    const matrix = mockData.matrices.find(
      (matrix) => matrix.id.toString() === matrixId
    )
    if (matrix == null) {
      return notFound()
    }
    return HttpResponse.json(matrix)
  }),
  http.delete<{
    matrixId: string
  }>('/api/matrices/:matrixId', async ({ params }) => {
    const { matrixId } = params
    mockData.matrices = mockData.matrices.filter(
      (matrix) => matrix.id.toString() !== matrixId
    )
    return noContent()
  }),

  // ok-programs
  http.get('/api/ok-programs', async () => {
    return HttpResponse.json(mockData.okPrograms)
  }),
  http.post<never, CreateOkProgram, OkProgram>(
    '/api/ok-programs',
    async ({ request }) => {
      const createOkProgram: CreateOkProgram = await request.json()
      const okProgram: OkProgram = {
        id: createMockId(),
        number: createOkProgram.number,
        name: createOkProgram.name,
        etag: `etag-${Date.now()}`
      }
      mockData.okPrograms.push(okProgram)
      return HttpResponse.json(okProgram, { status: 201 })
    }
  ),
  http.get<{ okProgramId: string }>(
    '/api/ok-programs/:okProgramId',
    async ({ params }) => {
      const { okProgramId } = params
      const okProgram = mockData.okPrograms.find(
        (r) => r.id.toString() === okProgramId
      )
      if (okProgram == null) {
        return new HttpResponse(null, { status: 404 })
      }
      return HttpResponse.json(okProgram)
    }
  ),
  http.put<{ okProgramId: string }, UpdateOkProgram, OkProgram>(
    '/api/ok-programs/:okProgramId',
    async ({ params, request }) => {
      const { okProgramId } = params
      const body: UpdateOkProgram = await request.json()
      const idx = mockData.okPrograms.findIndex(
        (r) => r.id.toString() === okProgramId
      )
      if (idx === -1) {
        return new HttpResponse(null, { status: 404 })
      }
      const ifMatch = request.headers.get('If-Match')
      if (ifMatch !== mockData.okPrograms[idx].etag) {
        return new HttpResponse(null, { status: 412 })
      }
      const updated: OkProgram = {
        ...mockData.okPrograms[idx],
        number: body.number,
        name: body.name,
        etag: `etag-${Date.now()}`
      }
      mockData.okPrograms[idx] = updated
      return HttpResponse.json(updated)
    }
  ),
  http.delete<{ okProgramId: string }>(
    '/api/ok-programs/:okProgramId',
    async ({ params }) => {
      const { okProgramId } = params
      mockData.okPrograms = mockData.okPrograms.filter(
        (r) => r.id.toString() !== okProgramId
      )
      return noContent()
    }
  ),

  // purposes
  http.get('/api/purposes', async () => {
    return HttpResponse.json(mockData.purposes)
  }),
  http.post<never, CreatePurpose, Purpose>(
    '/api/purposes',
    async ({ request }) => {
      const createPurpose: CreatePurpose = await request.json()
      const purpose: Purpose = {
        id: createMockId(),
        name: createPurpose.name,
        etag: `etag-${Date.now()}`
      }
      mockData.purposes.push(purpose)
      return HttpResponse.json(purpose, { status: 201 })
    }
  ),
  http.get<{ purposeId: string }>(
    '/api/purposes/:purposeId',
    async ({ params }) => {
      const { purposeId } = params
      const purpose = mockData.purposes.find(
        (p) => p.id.toString() === purposeId
      )
      if (purpose == null) {
        return new HttpResponse(null, { status: 404 })
      }
      return HttpResponse.json(purpose)
    }
  ),
  http.put<{ purposeId: string }, UpdatePurpose, Purpose>(
    '/api/purposes/:purposeId',
    async ({ params, request }) => {
      const { purposeId } = params
      const body: UpdatePurpose = await request.json()
      const idx = mockData.purposes.findIndex(
        (p) => p.id.toString() === purposeId
      )
      if (idx === -1) {
        return new HttpResponse(null, { status: 404 })
      }
      const ifMatch = request.headers.get('If-Match')
      if (ifMatch !== mockData.purposes[idx].etag) {
        return new HttpResponse(null, { status: 412 })
      }
      const updated: Purpose = {
        ...mockData.purposes[idx],
        name: body.name,
        etag: `etag-${Date.now()}`
      }
      mockData.purposes[idx] = updated
      return HttpResponse.json(updated)
    }
  ),
  http.delete<{ purposeId: string }>(
    '/api/purposes/:purposeId',
    async ({ params }) => {
      const { purposeId } = params
      mockData.purposes = mockData.purposes.filter(
        (p) => p.id.toString() !== purposeId
      )
      return noContent()
    }
  ),

  // sub-purposes
  http.get('/api/sub-purposes', async () => {
    return HttpResponse.json(mockData.subPurposes)
  }),
  http.post<never, CreateSubPurpose, SubPurpose>(
    '/api/sub-purposes',
    async ({ request }) => {
      const createSubPurpose: CreateSubPurpose = await request.json()
      const subPurpose: SubPurpose = {
        id: createMockId(),
        name: createSubPurpose.name,
        etag: `etag-${Date.now()}`
      }
      mockData.subPurposes.push(subPurpose)
      return HttpResponse.json(subPurpose, { status: 201 })
    }
  ),
  http.get<{ subPurposeId: string }>(
    '/api/sub-purposes/:subPurposeId',
    async ({ params }) => {
      const { subPurposeId } = params
      const subPurpose = mockData.subPurposes.find(
        (p) => p.id.toString() === subPurposeId
      )
      if (subPurpose == null) {
        return new HttpResponse(null, { status: 404 })
      }
      return HttpResponse.json(subPurpose)
    }
  ),
  http.put<{ subPurposeId: string }, UpdateSubPurpose, SubPurpose>(
    '/api/sub-purposes/:subPurposeId',
    async ({ params, request }) => {
      const { subPurposeId } = params
      const body: UpdateSubPurpose = await request.json()
      const idx = mockData.subPurposes.findIndex(
        (p) => p.id.toString() === subPurposeId
      )
      if (idx === -1) {
        return new HttpResponse(null, { status: 404 })
      }
      const ifMatch = request.headers.get('If-Match')
      if (ifMatch !== mockData.subPurposes[idx].etag) {
        return new HttpResponse(null, { status: 412 })
      }
      const updated: SubPurpose = {
        ...mockData.subPurposes[idx],
        name: body.name,
        etag: `etag-${Date.now()}`
      }
      mockData.subPurposes[idx] = updated
      return HttpResponse.json(updated)
    }
  ),
  http.delete<{ subPurposeId: string }>(
    '/api/sub-purposes/:subPurposeId',
    async ({ params }) => {
      const { subPurposeId } = params
      mockData.subPurposes = mockData.subPurposes.filter(
        (p) => p.id.toString() !== subPurposeId
      )
      return noContent()
    }
  ),

  // products
  http.post<never, CreateProduct, Product>(
    '/api/products',
    async ({ request }) => {
      const createProduct: CreateProduct = await request.json()
      const product: Product = {
        id: createMockId(),
        name: createProduct.name,
        code: createProduct.code
      }
      mockData.products.push(product)
      return HttpResponse.json(product)
    }
  ),
  http.get('/api/products', async () => {
    return HttpResponse.json(mockData.products)
  }),
  http.get<{
    productId: string
  }>('/api/products/:productId', async ({ params }) => {
    const { productId } = params
    const product = mockData.products.find(
      (product) => product.id.toString() === productId
    )
    if (product == null) {
      return notFound()
    }
    return HttpResponse.json(product)
  }),
  http.delete<{
    productId: string
  }>('/api/products/:productId', async ({ params }) => {
    const { productId } = params
    mockData.products = mockData.products.filter(
      (product) => product.id.toString() !== productId
    )
    return noContent()
  }),

  // substances
  http.post<never, CreateSubstance, Substance>(
    '/api/substances',
    async ({ request }) => {
      const createSubstance: CreateSubstance = await request.json()
      const substance: Substance = {
        id: createMockId(),
        name: createSubstance.name,
        code: createSubstance.code
      }
      mockData.substances.push(substance)
      return HttpResponse.json(substance)
    }
  ),
  http.get('/api/substances', async () => {
    return HttpResponse.json(mockData.substances)
  }),
  http.get<{
    substanceId: string
  }>('/api/substances/:substanceId', async ({ params }) => {
    const { substanceId } = params
    const substance = mockData.substances.find(
      (substance) => substance.id.toString() === substanceId
    )
    if (substance == null) {
      return notFound()
    }
    return HttpResponse.json(substance)
  }),
  http.delete<{
    substanceId: string
  }>('/api/substances/:substanceId', async ({ params }) => {
    const { substanceId } = params
    mockData.substances = mockData.substances.filter(
      (substance) => substance.id.toString() !== substanceId
    )
    return noContent()
  }),

  // tilsynsobjekter
  http.get(
    '/api/tilsynsobjekt-api/tilsynsobjekter/id/:id',
    async ({ params }) => {
      const tilsynsobjekt = mockData.tilsynsobjekter.find(
        (item) => item.idstring === params.id
      )
      return tilsynsobjekt ? HttpResponse.json(tilsynsobjekt) : notFound()
    }
  ),
  http.get('/api/tilsynsobjekt-api/v2/tilsynsobjekter', async ({ request }) => {
    const url = new URL(request.url)
    const search = url.searchParams.get('search')
    const searchDecoded = search ? decodeURIComponent(search) : null
    if (searchDecoded != null) {
      const response: TilsynsobjektResponseV2 = {
        body: {
          _embedded: {
            tilsynsobjektList: mockData.tilsynsobjekter.filter(
              (tilsynsobjekt) =>
                tilsynsobjekt.navn
                  ?.toLowerCase()
                  .includes(searchDecoded.toLowerCase())
            )
          }
        }
      }
      return HttpResponse.json(response)
    }
    return HttpResponse.json(mockData.tilsynsobjekter)
  }),
  // sampntTypes
  http.get('/api/efsa/sampnt', async () => {
    return HttpResponse.json(mockData.sampntTypes)
  }),
  http.post('/api/efsa/sampnt', async ({ request }) => {
    const body = (await request.json()) as {
      code: string
      nameEn: string
      nameNo: string
    }
    const sampntType = { id: createMockId(), ...body }
    mockData.sampntTypes.push(sampntType)
    return HttpResponse.json(sampntType, { status: 201 })
  }),
  http.get<{
    sampntTypeId: string
  }>('/api/efsa/sampnt/:sampntTypeId', async ({ params }) => {
    const { sampntTypeId } = params
    const sampntType = mockData.sampntTypes.find(
      (sampntType) => sampntType.id.toString() === sampntTypeId
    )
    if (sampntType == null) {
      return notFound()
    }
    return HttpResponse.json(sampntType)
  }),
  http.delete<{ sampntTypeId: string }>(
    '/api/efsa/sampnt/:sampntTypeId',
    async ({ params }) => {
      mockData.sampntTypes = mockData.sampntTypes.filter(
        (sampntType) => sampntType.id.toString() !== params.sampntTypeId
      )
      return noContent()
    }
  ),
  // news
  http.get('/api/news', async () => {
    return HttpResponse.json(mockData.news)
  }),
  http.post<never, AddNewsArticle, NewsArticle>(
    '/api/news',
    async ({ request }) => {
      const createNewsArticle: AddNewsArticle = await request.json()
      const news: NewsArticle = {
        id: createMockId(),
        subject: createNewsArticle.subject,
        date: createNewsArticle.date,
        app: createNewsArticle.app,
        endDate: createNewsArticle.endDate,
        icon: createNewsArticle.icon
      }
      mockData.news.push(news)
      return HttpResponse.json(news)
    }
  ),
  http.get<{
    articleId: string
  }>('/api/news/:articleId', async ({ params }) => {
    const { articleId } = params
    const article = mockData.news.find(
      (newsArticle) => newsArticle.id.toString() === articleId
    )
    if (article == null) {
      return notFound()
    }
    return HttpResponse.json(article)
  }),
  http.delete<{
    articleId: string
  }>('/api/news/:articleId', async ({ params }) => {
    const { articleId } = params
    mockData.news = mockData.news.filter(
      (newsArticle) => newsArticle.id.toString() !== articleId
    )
    return noContent()
  }),
  http.put<
    {
      articleId: string
    },
    UpdateNewsArticle,
    NewsArticle
  >('/api/news/:articleId', async ({ params, request }) => {
    const { articleId } = params
    const updatedArticle = await request.json()
    const articleIndex = mockData.news.findIndex(
      (newsArticle) => newsArticle.id.toString() === articleId
    )
    if (articleIndex === -1) {
      return HttpResponse.json(null, {
        status: 404,
        headers: { 'Content-Length': '0' }
      })
    }
    mockData.news[articleIndex] = {
      ...mockData.news[articleIndex],
      ...updatedArticle
    }
    return HttpResponse.json(mockData.news[articleIndex])
  }),
  http.get('/api/news/latest/proveta', async () => {
    const filteredNewsList: NewsArticles = []
    for (const newsArticle of mockData.news) {
      const now: Date = new Date('2024-05-05T15:00:00Z')
      const theEndDate = new Date(newsArticle.endDate)
      if (now < theEndDate && newsArticle.app === 'ProveTa') {
        const article = { ...newsArticle }
        filteredNewsList.push(article)
      }
    }
    filteredNewsList.sort(
      (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
    )
    if (filteredNewsList.length === 0) return HttpResponse.json([])
    const rightArticle = mockData.news.findIndex(
      (newsArticle) => newsArticle.id === filteredNewsList[0].id
    )
    const noArticle: NewsArticles = []
    if (rightArticle === -1) {
      return HttpResponse.json(noArticle)
    }
    return HttpResponse.json(mockData.news[rightArticle])
  }),
  http.get('/api/news/latest/proveplan', async () => {
    const filteredNewsList: NewsArticles = []
    for (const newsArticle of mockData.news) {
      const now: Date = new Date('2024-05-05T15:00:00Z')
      const theEndDate = new Date(newsArticle.endDate)
      if (now < theEndDate && newsArticle.app === 'ProvePlan') {
        const article = { ...newsArticle }
        filteredNewsList.push(article)
      }
    }
    filteredNewsList.sort(
      (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
    )
    if (filteredNewsList.length === 0) return HttpResponse.json([])
    const rightArticle = mockData.news.findIndex(
      (newsArticle) => newsArticle.id === filteredNewsList[0].id
    )
    const noArticle: NewsArticles = []
    if (rightArticle === -1) {
      return HttpResponse.json(noArticle)
    }
    return HttpResponse.json(mockData.news[rightArticle])
  }),
  http.post<{
    articleId: string
  }>('/api/news/:articleId/image', async ({ params }) => {
    const { articleId } = params
    const article = mockData.news.find(
      (newsArticle) => newsArticle.id.toString() === articleId
    )
    if (article == null) {
      return notFound()
    }
    return new HttpResponse(null, {
      status: 200
    })
  }),
  // produsenter
  http.get('/api/tilsynsobjekt-api/produsenter', async ({ request }) => {
    const url = new URL(request.url)
    const search: string | null = url.searchParams.get('search')
    if (search != null) {
      const response: ProdusentResponse = {
        _embedded: {
          produsentList: mockData.produsenter.filter((produsent) => {
            return (
              (produsent.virksomhetsNavn
                ?.toLowerCase()
                .includes(search.toLowerCase()) ??
                false) ||
              (produsent.produsentnummer?.includes(search.toLowerCase()) ??
                false)
            )
          })
        }
      }
      return HttpResponse.json(response)
    }
    return HttpResponse.json(mockData.produsenter)
  }),
  // ldf producer geo
  http.get('/api/ldf/geo/prodnr/:prodnr', async ({ params }) => {
    const { prodnr } = params
    if (prodnr === '1111111111') {
      return HttpResponse.json([
        {
          prodnr: '1111111111',
          kommunenr: '0301',
          fylke: 'Oslo',
          foretaksnavn: 'Virksomhet 1'
        }
      ])
    }
    if (prodnr === '2222222222') {
      return HttpResponse.json([
        {
          prodnr: '2222222222',
          kommunenr: '4601',
          fylke: 'Vestland',
          foretaksnavn: 'Virksomhet 2'
        }
      ])
    }
    const matchedProducer = mockData.produsenter.find(
      (p) => p.produsentnummer === prodnr
    )
    if (matchedProducer) {
      return HttpResponse.json([
        {
          prodnr: matchedProducer.produsentnummer,
          kommunenr: '0301',
          fylke: 'Oslo',
          foretaksnavn: matchedProducer.virksomhetsNavn ?? 'Test Gård'
        }
      ])
    }
    return HttpResponse.json([])
  }),
  // payload-schemas
  http.post<never, CreatePayloadSchema, PayloadSchema>(
    '/api/payload-schemas',
    async ({ request }) => {
      const createPayloadSchema: CreatePayloadSchema = await request.json()
      const payloadSchema: PayloadSchema = {
        id: createMockId(),
        name: createPayloadSchema.name,
        schema: createPayloadSchema.schema,
        userId: 1,
        lastUpdated: new Date().toISOString(),
        etag: 'etag'
      }
      mockData.payloadSchemas.push(payloadSchema)
      return HttpResponse.json(payloadSchema)
    }
  ),
  http.get('/api/payload-schemas', async ({ request }) => {
    const url = new URL(request.url)
    const labId = url.searchParams.get('labId')
    const schemas = labId
      ? mockData.payloadSchemas.filter(
          (schema) => schema.labId?.toString() === labId
        )
      : mockData.payloadSchemas
    return HttpResponse.json(schemas)
  }),
  http.get<{
    payloadSchemaId: string
  }>('/api/payload-schemas/:payloadSchemaId', async ({ params }) => {
    const { payloadSchemaId } = params
    const payloadSchema = mockData.payloadSchemas.find(
      (payloadSchema) => payloadSchema.id.toString() === payloadSchemaId
    )
    if (payloadSchema == null) {
      return notFound()
    }
    return HttpResponse.json(payloadSchema)
  }),
  http.put<{ payloadSchemaId: string }, UpdatePayloadSchema, PayloadSchema>(
    '/api/payload-schemas/:payloadSchemaId',
    async ({ params, request }) => {
      const index = mockData.payloadSchemas.findIndex(
        (schema) => schema.id.toString() === params.payloadSchemaId
      )
      if (index === -1) return notFound()
      const body = await request.json()
      const updated = {
        ...mockData.payloadSchemas[index],
        ...body,
        lastUpdated: new Date().toISOString(),
        etag: newEtag()
      }
      mockData.payloadSchemas[index] = updated
      return HttpResponse.json(updated)
    }
  ),
  http.delete<{
    payloadSchemaId: string
  }>('/api/payload-schemas/:payloadSchemaId', async ({ params }) => {
    const { payloadSchemaId } = params
    mockData.payloadSchemas = mockData.payloadSchemas.filter(
      (payloadSchema) => payloadSchema.id.toString() !== payloadSchemaId
    )
    return noContent()
  }),
  // ansatte/self
  http.get('/api/orgenhet-api/ansatte/self', async () => {
    const response: Ansatt = {
      navn: 'Self',
      regionId: 'r1',
      avdelingId: 'a1',
      seksjonId: 's1'
    }
    return HttpResponse.json(response)
  }),
  // regioner
  http.get('/api/orgenhet-api/regioner', async () => {
    const response: RegionResponse = {
      _embedded: {
        regionList: mockData.regioner
      }
    }
    return HttpResponse.json(response)
  }),
  http.get('/api/orgenhet-api/regioner/:regionId', async ({ params }) => {
    const region = mockData.regioner.find(
      (region) => region.id === params.regionId
    )
    return region ? HttpResponse.json(region) : notFound()
  }),
  // avdelinger
  http.get('/api/orgenhet-api/avdelinger', async () => {
    const response: AvdelingResponse = {
      _embedded: {
        avdelingList: mockData.avdelinger
      }
    }
    return HttpResponse.json(response)
  }),
  http.get('/api/orgenhet-api/avdelinger/:avdelingId', async ({ params }) => {
    const { avdelingId } = params
    const avdeling = mockData.avdelinger.find(
      (avdeling) => avdeling.id.toString() === avdelingId
    )
    if (avdeling == null) {
      return notFound()
    }
    return HttpResponse.json(avdeling)
  }),
  http.get<{
    regionId: string
  }>('/api/orgenhet-api/regioner/:regionId/avdelinger', async ({ params }) => {
    const { regionId } = params
    const response: AvdelingResponse = {
      _embedded: {
        avdelingList: mockData.avdelinger.filter(
          (avdeling) => avdeling.regionId === regionId
        )
      }
    }
    return HttpResponse.json(response)
  }),
  // seksjoner
  http.get(
    '/api/orgenhet-api/avdelinger/:avdelingId/seksjoner',
    async ({ params }) => {
      const { avdelingId } = params
      const response: SeksjonResponse = {
        _embedded: {
          seksjonList: mockData.seksjoner.filter(
            (seksjon) => seksjon.avdelingId?.toString() === avdelingId
          )
        }
      }
      return HttpResponse.json(response)
    }
  ),
  http.get('/api/orgenhet-api/seksjoner/:seksjonId', async ({ params }) => {
    const seksjon = mockData.seksjoner.find(
      (seksjon) => seksjon.id === params.seksjonId
    )
    return seksjon ? HttpResponse.json(seksjon) : notFound()
  }),
  http.get(
    '/api/orgenhet-api/seksjoner/:seksjonId/team',
    async ({ params }) => {
      // Speiler backend, som slår opp seksjonens avdeling og henter
      // `seksjon_id = X OR avdeling_id = Y`. Teamene som henger rett på
      // avdelingen kommer altså med under hver seksjon i avdelingen.
      const seksjon = mockData.seksjoner.find(
        (seksjon) => seksjon.id === params.seksjonId
      )
      return HttpResponse.json(
        mockData.teams.filter(
          (team) =>
            team.seksjonId === params.seksjonId ||
            (seksjon?.avdelingId != null &&
              team.avdelingId === seksjon.avdelingId)
        )
      )
    }
  ),
  http.get('/api/orgenhet-api/team', async () => {
    return HttpResponse.json(mockData.teams)
  }),
  http.get('/api/orgenhet-api/team/:teamId', async ({ params }) => {
    const team = mockData.teams.find(
      (team) => team.id.toString() === params.teamId
    )
    return team ? HttpResponse.json(team) : notFound()
  }),
  http.post('/api/orgenhet-api/team', async ({ request }) => {
    const body = (await request.json()) as {
      name: string
      seksjonId?: string
      avdelingId?: string
    }
    const team = {
      id: createMockId(),
      name: body.name,
      seksjonId: body.seksjonId,
      avdelingId: body.avdelingId
    }
    mockData.teams.push(team)
    return HttpResponse.json(team, { status: 201 })
  }),
  http.put('/api/orgenhet-api/team/:teamId', async ({ params, request }) => {
    const { teamId } = params
    const body = (await request.json()) as {
      name: string
      seksjonId?: string
      avdelingId?: string
    }
    const idx = mockData.teams.findIndex(
      (team) => team.id.toString() === teamId
    )
    if (idx === -1) {
      return notFound()
    }
    const updated = {
      ...mockData.teams[idx],
      name: body.name,
      seksjonId: body.seksjonId,
      avdelingId: body.avdelingId
    }
    mockData.teams[idx] = updated
    return HttpResponse.json(updated)
  }),
  http.delete('/api/orgenhet-api/team/:teamId', async ({ params }) => {
    const { teamId } = params
    mockData.teams = mockData.teams.filter(
      (team) => team.id.toString() !== teamId
    )
    return noContent()
  }),
  http.get(
    '/api/orgenhet-api/avdelinger/:avdelingId/ansatte',
    async ({ params }) => {
      return HttpResponse.json(
        mockData.ansatte.filter(
          (ansatt) => ansatt.avdelingId === params.avdelingId
        )
      )
    }
  ),
  http.get('/api/orgenhet-api/ansatte/:ansattId', async ({ params }) => {
    const ansatt = mockData.ansatte.find(
      (ansatt) => ansatt.id === params.ansattId
    )
    return ansatt ? HttpResponse.json(ansatt) : notFound()
  }),

  http.get('/api/fiskeridir/site/:siteNr/license-connections', async () => {
    return HttpResponse.json([
      {
        openLegalEntityNr: '999999999',
        legalEntityName: 'Test juridisk enhet'
      }
    ])
  }),
  http.get('/api/geo/elvenett', async ({ request }) => {
    const url = new URL(request.url)
    const lon = url.searchParams.get('lon')
    const lat = url.searchParams.get('lat')
    if (!lon || !lat) {
      return new HttpResponse(null, { status: 400 })
    }
    return HttpResponse.json([
      {
        vassdragsnummer: '002.A1',
        elvenavn: 'Glomma',
        elveid: '1001'
      },
      {
        vassdragsnummer: '002.A2',
        elvenavn: 'Vorma',
        elveid: '1002'
      }
    ])
  }),
  http.get('/api/husdyrregister/storfe/:individmerke', async ({ params }) => {
    return HttpResponse.json({
      individmerke: params.individmerke,
      produsentnr: '1111111111',
      kjonn: 'Ku'
    })
  }),
  http.get('/api/dashboard/status-duration-report', async () => {
    return HttpResponse.json(mockData.statusDurationReports)
  }),
  http.get('/api/reports/sample-taking', async ({ request }) => {
    const url = new URL(request.url)
    const from = url.searchParams.get('from') ?? '2026-01-01'
    const through = url.searchParams.get('through') ?? '2026-12-31'
    const eftaCode = url.searchParams.get('eftaCode')
    const emptyActivity = {
      physicalSampleCount: 0,
      performedUnitCount: 0,
      analyzedSubsampleCount: 0,
      positiveSampleCount: 0,
      positiveSubsampleCount: 0
    }
    return HttpResponse.json({
      period: { from, through, timeZone: 'Europe/Oslo' },
      summary: {
        activity: emptyActivity,
        planExecution: eftaCode
          ? undefined
          : { plannedUnitCount: 0, performedUnitCount: 0 }
      },
      timeSeries: Array.from({ length: 12 }, (_, index) => ({
        period: {
          from: `${from.slice(0, 4)}-${String(index + 1).padStart(2, '0')}-01`,
          through: `${from.slice(0, 4)}-${String(index + 1).padStart(2, '0')}-28`
        },
        measures: {
          activity: emptyActivity,
          planExecution: eftaCode
            ? undefined
            : { plannedUnitCount: 0, performedUnitCount: 0 }
        }
      })),
      breakdowns: { plans: [], owners: [], products: [] },
      facets: { eftaCodes: [] }
    })
  }),
  http.get('/api/orgenhet-api/organisasjonsenheter', async () =>
    HttpResponse.json([])
  ),

  // users
  http.get('/api/users/me', async () =>
    HttpResponse.json({
      id: 'mock',
      type: 'mattilsynet',
      roles: ['employee', 'admin'],
      permissions: []
    })
  ),
  http.get('/api/users', async ({ request }) => {
    const search =
      new URL(request.url).searchParams
        .get('search')
        ?.trim()
        .toLocaleLowerCase('nb') ?? ''
    return HttpResponse.json(
      mockData.users.filter((user) =>
        [user.name, user.id, user.employeeId, user.email].some((value) =>
          value?.toLocaleLowerCase('nb').includes(search)
        )
      )
    )
  }),
  http.get<{ userId: string }>(
    '/api/users/:userId/permissions',
    ({ params }) => {
      const user = mockData.users.find(({ id }) => id === params.userId)
      return user ? HttpResponse.json(user.permissions ?? []) : notFound()
    }
  ),
  http.post<
    { userId: string },
    { permissions: components['schemas']['AddUserPermission'][] }
  >('/api/users/:userId/permissions', async ({ params, request }) => {
    const user = mockData.users.find(({ id }) => id === params.userId)
    if (!user) return notFound()
    const body = await request.json()
    user.permissions ??= []
    let nextId =
      Math.max(
        0,
        ...mockData.users.flatMap(({ permissions }) =>
          (permissions ?? []).map(({ id }) => id)
        )
      ) + 1
    const added = body.permissions
      .filter(
        ({ externalId, responsibility }) =>
          !user.permissions?.some(
            (permission) =>
              permission.externalId === externalId &&
              permission.responsibility === responsibility
          )
      )
      .map(({ externalId, responsibility }) => ({
        id: nextId++,
        externalId,
        responsibility
      }))
    user.permissions.push(...added)
    return HttpResponse.json(user.permissions, { status: 201 })
  }),
  http.delete<{ userId: string; permissionId: string }>(
    '/api/users/:userId/permissions/:permissionId',
    ({ params }) => {
      const user = mockData.users.find(({ id }) => id === params.userId)
      if (
        !user?.permissions?.some(({ id }) => id === Number(params.permissionId))
      )
        return notFound()
      user.permissions = user.permissions.filter(
        ({ id }) => id !== Number(params.permissionId)
      )
      return new HttpResponse(null, { status: 204 })
    }
  ),
  http.get<{
    userId: string
  }>('/api/users/:userId', async ({ params }) => {
    const { userId } = params
    const user = mockData.users.find((user) => user.id.toString() === userId)
    if (user == null) {
      return notFound()
    }
    return HttpResponse.json(user)
  }),
  http.get<{
    userId: string
  }>('/api/users/:userId/settings', async ({ params }) => {
    const { userId } = params
    const user = mockData.users.find((user) => user.id.toString() === userId)
    if (user == null) {
      return notFound()
    }
    return HttpResponse.json({
      settings: user.settings
    })
  }),
  http.put<{ userId: string }, UpdateUserSettings, UpdateUserSettings>(
    '/api/users/:userId/settings',
    async ({ params, request }) => {
      const { userId } = params
      const updatedSettings: UpdateUserSettings = await request.json()
      if (updatedSettings != null) {
        const user = mockData.users.find(
          (user) => user.id.toString() === userId
        )
        if (user != null) {
          user.settings = updatedSettings.settings
        }
        return HttpResponse.json({
          settings: user?.settings ?? ''
        })
      }
    }
  ),
  // Log
  http.post('/api/system/log', async () => {
    return HttpResponse.json()
  }),
  http.get('/api/batch', async () => {
    return HttpResponse.json({
      matrices: mockData.matrices,
      products: mockData.products,
      substances: mockData.substances,
      sampleTypes: mockData.sampleTypes,
      analysisTypes: mockData.analysisTypes,
      payloadSchemas: mockData.payloadSchemas,
      labs: mockData.labs,
      operations: [],
      sampnts: mockData.sampntTypes,
      ordningsverdier: mockData.ordningsverdier
    })
  })
]
